import { useState, useEffect, useCallback, useRef } from "react";
import { getWoodrowAccount, getWoodrowTrades, woodrowRunNow, woodrowReoptimize, woodrowReset, woodrowResume, getMarketStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import WoodrowPerformance from "@/components/WoodrowPerformance";
import WoodrowBenchmark from "@/components/WoodrowBenchmark";
import WoodrowStrategy from "@/components/WoodrowStrategy";
import ReoptimizeResultsDialog from "@/components/ReoptimizeResultsDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Bot, Play, RotateCcw, TrendingUp, TrendingDown, DollarSign,
  Activity, Clock, ChevronDown, ChevronRight, Loader2, ShieldAlert, ShieldCheck, AlertTriangle, PlayCircle, Volume2, VolumeX, Shuffle
} from "lucide-react";
import { toast } from "sonner";

export default function WoodrowBot() {
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradesOpen, setTradesOpen] = useState(true);
  const [running, setRunning] = useState(false);
  const [reoptimizing, setReoptimizing] = useState(false);
  const [reoptResult, setReoptResult] = useState(null);
  const [now, setNow] = useState(new Date());
  const [perfPeriod, setPerfPeriod] = useState("1d");
  const [mktStatus, setMktStatus] = useState(null);
  const [speakerOn, setSpeakerOn] = useState(false);
  const lastTradeCount = useRef(0);
  const speakerRef = useRef(false);
  const speakerToggling = useRef(false);

  // Keep ref in sync so the callback can read latest value
  useEffect(() => { speakerRef.current = speakerOn; }, [speakerOn]);

  const announce = useCallback((text) => {
    if (!speakerRef.current || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 0.85;
    utterance.volume = 1;
    utterance.lang = "en-GB";
    // Pick a British voice
    const voices = window.speechSynthesis.getVoices();
    const british = voices.find(v =>
      v.lang === "en-GB" && (v.name.includes("Daniel") || v.name.includes("Male") || !v.name.includes("Female"))
    ) || voices.find(v => v.lang === "en-GB") || voices.find(v => v.lang.startsWith("en-GB"));
    if (british) utterance.voice = british;
    window.speechSynthesis.speak(utterance);
  }, []);

  // Live clock - tick every second
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const fetch = useCallback(() => {
    Promise.all([
      getWoodrowAccount().catch(() => ({ data: null })),
      getWoodrowTrades().catch(() => ({ data: [] })),
      getMarketStatus().catch(() => ({ data: null })),
    ]).then(([a, t, ms]) => {
      const newTrades = t.data || [];
      // Detect new trades and announce
      if (newTrades.length > lastTradeCount.current && lastTradeCount.current > 0) {
        const newOnes = newTrades.slice(0, newTrades.length - lastTradeCount.current);
        newOnes.forEach(trade => {
          const pnlText = trade.pnl !== undefined ? ` for ${trade.pnl >= 0 ? "a gain" : "a loss"} of $${Math.abs(trade.pnl).toLocaleString()}` : "";
          const triggerText = trade.trigger ? ` triggered by ${trade.trigger.replace("-", " ")}` : "";
          const actionWord = {BUY: "bought", SELL: "sold", SHORT: "shorted", COVER: "covered"}[trade.action] || trade.action;
          const msg = `Woodrow ${actionWord} ${trade.shares} shares of ${trade.symbol} at $${trade.price.toLocaleString()}${pnlText}${triggerText}. ${trade.reason || ""}`;
          announce(msg);
        });
      }
      lastTradeCount.current = newTrades.length;
      setAccount(a.data);
      setTrades(newTrades);
      setMktStatus(ms.data);
      setLoading(false);
    });
  }, [announce]);

  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [fetch]);

  const handleReoptimize = async () => {
    setReoptimizing(true);
    try {
      const { data } = await woodrowReoptimize();
      setReoptResult(data);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reoptimize failed");
    }
    setReoptimizing(false);
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const { data: started } = await woodrowRunNow();
      toast.success(started?.initial_build_at_close
        ? "Market is closed. Woodrow is building your starting portfolio at last close prices..."
        : started?.initial_build ? "Woodrow is building your starting portfolio at live prices..." : "Woodrow is evaluating the market...");
      // Poll for completion
      const poll = setInterval(async () => {
        const res = await getWoodrowAccount().catch(() => null);
        if (res?.data?.status !== "evaluating") {
          clearInterval(poll);
          setRunning(false);
          fetch();
          toast.success("Woodrow completed evaluation");
        }
      }, 3000);
      setTimeout(() => { clearInterval(poll); setRunning(false); }, 60000);
    } catch (e) {
      setRunning(false);
      toast.error(e.response?.data?.detail || "Failed to run");
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset Woodrow to $100,000? This clears all positions, trade history, and resets the S&P benchmark tracker. Everyone starts fresh.")) return;
    try {
      await woodrowReset();
      toast.success("Woodrow's account reset to $100,000");
      fetch();
    } catch {
      toast.error("Failed to reset");
    }
  };

  const handleResume = async () => {
    try {
      await woodrowResume();
      toast.success("Woodrow resumed trading");
      fetch();
    } catch {
      toast.error("Failed to resume");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="woodrow-bot-loading">
        <Skeleton className="h-40 w-full bg-bt-surface-2" />
        <Skeleton className="h-32 w-full bg-bt-surface-2" />
      </div>
    );
  }

  if (!account) return null;

  const returnPositive = account.total_return >= 0;
  const isEvaluating = account.status === "evaluating" || running;
  const isPaused = account.paused;

  return (
    <div data-testid="woodrow-bot">
      <ReoptimizeResultsDialog result={reoptResult} onClose={() => setReoptResult(null)} />
      {account.queued_swaps?.length > 0 && (
        <div className="bg-bt-gold/5 border border-bt-gold/30 px-4 py-2 mb-4 flex items-center gap-2 text-xs font-mono text-bt-gold" data-testid="woodrow-queued-swaps">
          <Clock className="w-3.5 h-3.5" /> Queued for market open: {account.queued_swaps.map(q => `${q.sell} → ${q.buy}`).join(", ")}
        </div>
      )}
      {/* Paused Banner */}
      {isPaused && (
        <div className="bg-bt-negative/10 border border-bt-negative/30 px-4 py-3 flex items-center justify-between mb-4" data-testid="woodrow-paused-banner">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-bt-negative" />
            <div>
              <p className="font-mono text-sm font-medium text-bt-negative">TRADING PAUSED</p>
              <p className="text-xs text-bt-text-secondary">{account.pause_reason}</p>
            </div>
          </div>
          <Button size="sm" onClick={handleResume} className="bg-bt-positive text-bt-bg hover:bg-bt-positive/90 font-mono text-xs" data-testid="woodrow-resume-btn">
            <PlayCircle className="w-3 h-3 mr-1" /> Resume
          </Button>
        </div>
      )}

      {/* 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT COLUMN: Account + Assessment + Clock */}
        <div className="space-y-4">
          <div className="ai-glow p-3 sm:p-5">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-bt-gold/20 border border-bt-gold/40 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-heading text-sm sm:text-base font-medium text-bt-text-primary">Woodrow Trading Bot</h3>
                  <p className="text-[10px] text-bt-text-tertiary font-mono capitalize" data-testid="woodrow-risk-label">{account?.risk_tolerance || "moderate"} AI portfolio manager</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {isEvaluating && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-bt-gold/10 border border-bt-gold/30 text-[10px] font-mono text-bt-gold">
                    <Loader2 className="w-3 h-3 animate-spin" /> Evaluating
                  </span>
                )}
                <Button size="sm" onClick={handleRun} disabled={isEvaluating} className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-mono text-xs h-7 px-2" data-testid="woodrow-run-now-btn">
                  <Play className="w-3 h-3 mr-1" /> Run
                </Button>
                <Button size="sm" variant="outline" onClick={handleReoptimize} disabled={isEvaluating || reoptimizing || !account.positions?.length}
                  title="Surgically swap clearly underperforming positions — may change nothing"
                  className="border-bt-gold/40 text-bt-gold hover:bg-bt-gold/10 font-mono text-xs h-7 px-2" data-testid="woodrow-reoptimize-btn">
                  {reoptimizing ? <Loader2 className="w-3 h-3 sm:mr-1 animate-spin" /> : <Shuffle className="w-3 h-3 sm:mr-1" />}<span className="hidden sm:inline">Reoptimize</span>
                </Button>
                <Button size="sm" variant="outline" onClick={handleReset} className="border-bt-border-focus text-bt-text-secondary hover:text-bt-negative font-mono text-xs h-7 px-2" data-testid="woodrow-reset-btn">
                  <RotateCcw className="w-3 h-3" />
                </Button>
                <button
                  onClick={() => {
                    if (speakerToggling.current) return;
                    speakerToggling.current = true;
                    setTimeout(() => { speakerToggling.current = false; }, 1000);
                    setSpeakerOn(s => {
                      const next = !s;
                      window.speechSynthesis?.cancel();
                      if (next) {
                        lastTradeCount.current = trades.length;
                        const u = new SpeechSynthesisUtterance("Woodrow here. Audio enabled. I shall announce my trades forthwith.");
                        u.rate = 0.95; u.pitch = 0.85; u.lang = "en-GB";
                        const voices = window.speechSynthesis.getVoices();
                        const brit = voices.find(v => v.lang === "en-GB") || null;
                        if (brit) u.voice = brit;
                        window.speechSynthesis?.speak(u);
                      }
                      return next;
                    });
                  }}
                  className={`h-7 w-7 flex items-center justify-center transition-colors ${
                    speakerOn
                      ? "bg-bt-gold/20 border border-bt-gold/40 text-bt-gold"
                      : "border border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-primary"
                  }`}
                  data-testid="woodrow-speaker-toggle"
                  title={speakerOn ? "Mute announcements" : "Enable trade announcements"}
                >
                  {speakerOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Value Cards */}
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-3">
              <div className="bg-bt-surface-2 border border-bt-border-subtle px-2 sm:px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Portfolio</p>
                <p className="text-base sm:text-lg font-mono font-medium tracking-tighter text-bt-text-primary" data-testid="woodrow-portfolio-value">
                  ${account.portfolio_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-bt-surface-2 border border-bt-border-subtle px-2 sm:px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Cash</p>
                <p className="text-base sm:text-lg font-mono font-medium tracking-tighter text-bt-text-primary" data-testid="woodrow-cash">
                  ${account.cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-bt-surface-2 border border-bt-border-subtle px-2 sm:px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Return</p>
                <p className={`text-base sm:text-lg font-mono font-medium tracking-tighter ${returnPositive ? "text-bt-positive" : "text-bt-negative"}`} data-testid="woodrow-return">
                  {returnPositive ? "+" : ""}{account.total_return}%
                </p>
              </div>
              <div className="bg-bt-surface-2 border border-bt-border-subtle px-2 sm:px-3 py-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Total P&L</p>
                <p className={`text-base sm:text-lg font-mono font-medium tracking-tighter ${account.total_pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="woodrow-total-pnl">
                  {account.total_pnl >= 0 ? "+" : ""}${account.total_pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>

            {/* Realized vs Unrealized P&L */}
            {(account.realized_pnl !== undefined) && (
              <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-3" data-testid="woodrow-pnl-breakdown">
                <div className="bg-bt-surface-2 border border-bt-border-subtle px-2 sm:px-3 py-2">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary flex items-center gap-1">
                    Realized P&L <span className="text-[8px] text-bt-text-tertiary/60 normal-case tracking-normal">(sold)</span>
                  </p>
                  <p className={`text-sm sm:text-base font-mono font-medium tracking-tighter ${account.realized_pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="woodrow-realized-pnl">
                    {account.realized_pnl >= 0 ? "+" : ""}${account.realized_pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-bt-surface-2 border border-bt-border-subtle px-2 sm:px-3 py-2">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary flex items-center gap-1">
                    Unrealized P&L <span className="text-[8px] text-bt-text-tertiary/60 normal-case tracking-normal">(open)</span>
                  </p>
                  <p className={`text-sm sm:text-base font-mono font-medium tracking-tighter ${account.unrealized_pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="woodrow-unrealized-pnl">
                    {account.unrealized_pnl >= 0 ? "+" : ""}${account.unrealized_pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
            )}

            {/* AI Assessment */}
            {account.last_assessment && (
              <div className="bg-bt-surface-2 border border-bt-border-subtle p-3 mb-3">
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-gold mb-1">Assessment</p>
                <p className="text-xs text-bt-text-secondary font-heading italic leading-relaxed">"{account.last_assessment}"</p>
                {account.last_strategy && <p className="text-[10px] text-bt-text-tertiary font-body mt-1">{account.last_strategy}</p>}
              </div>
            )}

            {/* Risk Alerts */}
            {account.risk_alerts && account.risk_alerts.length > 0 && (
              <div className="bg-bt-negative/5 border border-bt-negative/20 p-2 mb-3" data-testid="woodrow-risk-alerts">
                <p className="text-[10px] font-mono uppercase tracking-wider text-bt-negative mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Risk Alerts</p>
                {account.risk_alerts.map((alert, i) => <p key={i} className="text-[10px] text-bt-text-secondary font-body ml-4">- {alert}</p>)}
              </div>
            )}

            {/* Risk Rules */}
            <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono text-bt-text-tertiary mb-3">
              <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-bt-positive" /> SL:{account.stop_loss_pct}%</span>
              <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-bt-gold" /> TS:{account.trailing_stop_pct}%</span>
              <span className="flex items-center gap-1"><ShieldAlert className="w-3 h-3 text-bt-negative" /> Floor:${account.drawdown_floor?.toLocaleString()}</span>
            </div>

            {/* Clock */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2 bg-bt-surface-2 border border-bt-border-subtle p-2">
              <div>
                <p className="text-[9px] font-mono uppercase text-bt-text-tertiary">Time</p>
                <p className="font-mono text-xs text-bt-text-primary">{now.toLocaleTimeString()}</p>
              </div>
              <div>
                <p className="text-[9px] font-mono uppercase text-bt-text-tertiary">Market</p>
                <p className={`font-mono text-xs font-medium ${mktStatus?.us_market === "open" ? "text-bt-positive" : mktStatus?.us_market === "pre-market" || mktStatus?.us_market === "after-hours" ? "text-bt-gold" : "text-bt-text-secondary"}`}>
                  {mktStatus?.us_market === "open" ? "OPEN" : mktStatus?.us_market === "pre-market" ? "PRE" : mktStatus?.us_market === "after-hours" ? "AH" : "CLOSED"}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-mono uppercase text-bt-text-tertiary">Last Trade</p>
                <p className="font-mono text-xs text-bt-text-primary">{trades.length > 0 ? new Date(trades[0].timestamp).toLocaleTimeString() : "—"}</p>
              </div>
              <div>
                <p className="text-[9px] font-mono uppercase text-bt-text-tertiary">Trades</p>
                <p className="font-mono text-xs text-bt-text-primary">{account.total_trades}</p>
              </div>
            </div>
          </div>

          {/* Positions */}
          {account.positions.length > 0 && (
            <div className="bg-bt-surface border border-bt-border-subtle" data-testid="woodrow-positions">
              <div className="px-3 py-2 border-b border-bt-border-subtle">
                <span className="text-xs font-mono uppercase tracking-wider text-bt-text-secondary">Positions ({account.positions.length})</span>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-bt-border-subtle hover:bg-transparent">
                      <TableHead className="text-bt-text-tertiary font-mono text-[10px] uppercase">Symbol</TableHead>
                      <TableHead className="text-bt-text-tertiary font-mono text-[10px] uppercase text-right">Shares</TableHead>
                      <TableHead className="text-bt-text-tertiary font-mono text-[10px] uppercase text-right">Current</TableHead>
                      <TableHead className="text-bt-text-tertiary font-mono text-[10px] uppercase text-right">Value</TableHead>
                      <TableHead className="text-bt-text-tertiary font-mono text-[10px] uppercase text-right">%</TableHead>
                      <TableHead className="text-bt-text-tertiary font-mono text-[10px] uppercase text-right">P&L</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {account.positions.map(p => (
                      <TableRow key={p.symbol} className="border-bt-border-subtle hover:bg-bt-hover">
                        <TableCell className="py-1.5">
                          <span className="font-mono text-xs font-medium text-bt-text-primary">{p.symbol}</span>
                          {p.position_type === "short" && (
                            <span className="ml-1 text-[9px] font-mono px-1 py-0.5 bg-bt-negative/15 text-bt-negative border border-bt-negative/30">SHORT</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-bt-text-primary py-1.5">
                          {p.shares % 1 === 0 ? p.shares : parseFloat(p.shares.toFixed(Math.min(6, 8 - Math.floor(p.shares).toString().length)))}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-bt-text-primary py-1.5">{p.current_price ? `$${p.current_price.toLocaleString()}` : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-bt-text-primary py-1.5">${p.market_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</TableCell>
                        <TableCell className="text-right font-mono text-[10px] text-bt-text-tertiary py-1.5">
                          {account.portfolio_value > 0 ? (Math.abs(p.market_value) / account.portfolio_value * 100).toFixed(1) + "%" : "—"}
                        </TableCell>
                        <TableCell className="text-right py-1.5">
                          <span className={`font-mono text-xs ${p.pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                            {p.pnl >= 0 ? "+" : ""}${p.pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals & Capital Allocation */}
                    {(() => {
                      const longs = account.positions.filter(p => p.position_type !== "short");
                      const shorts = account.positions.filter(p => p.position_type === "short");
                      const longValue = longs.reduce((sum, p) => sum + p.market_value, 0);
                      const totalPnl = account.positions.reduce((sum, p) => sum + p.pnl, 0);
                      const shortCollateral = shorts.reduce((sum, p) => sum + p.avg_price * p.shares, 0);
                      const cash = account.cash || 0;
                      const portfolio = longValue + shortCollateral + cash + shorts.reduce((s, p) => s + p.pnl, 0);
                      return (
                        <>
                          <TableRow className="border-t-2 border-bt-gold/30 hover:bg-transparent">
                            <TableCell className="py-1.5 pb-0.5" colSpan={4}>
                              <span className="font-mono text-[10px] text-bt-text-tertiary uppercase tracking-wider">Long Positions</span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-bt-text-primary py-1.5 pb-0.5">
                              ${longValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </TableCell>
                            <TableCell className="text-right py-1.5 pb-0.5">
                              <span className={`font-mono text-xs ${longs.reduce((s,p) => s+p.pnl, 0) >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                                {longs.reduce((s,p) => s+p.pnl, 0) >= 0 ? "+" : ""}${longs.reduce((s,p) => s+p.pnl, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </TableCell>
                          </TableRow>
                          {shorts.length > 0 && (
                            <TableRow className="hover:bg-transparent">
                              <TableCell className="py-0.5" colSpan={4}>
                                <span className="font-mono text-[10px] text-bt-text-tertiary uppercase tracking-wider">Short Collateral</span>
                                <span className="font-mono text-[9px] text-bt-text-tertiary ml-1">
                                  ({shorts.map(p => p.symbol).join(", ")})
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs text-bt-text-secondary py-0.5">
                                ${shortCollateral.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </TableCell>
                              <TableCell className="text-right py-0.5">
                                <span className={`font-mono text-xs ${shorts.reduce((s,p) => s+p.pnl, 0) >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                                  {shorts.reduce((s,p) => s+p.pnl, 0) >= 0 ? "+" : ""}${shorts.reduce((s,p) => s+p.pnl, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow className="hover:bg-transparent">
                            <TableCell className="py-0.5" colSpan={4}>
                              <span className="font-mono text-[10px] text-bt-text-tertiary uppercase tracking-wider">Cash Available</span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-bt-text-secondary py-0.5" colSpan={2}>
                              ${cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </TableCell>
                          </TableRow>
                          <TableRow className="border-t border-bt-gold/20 hover:bg-transparent">
                            <TableCell className="py-2" colSpan={4}>
                              <span className="font-mono text-xs font-medium text-bt-gold uppercase tracking-wider">Portfolio</span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs font-medium text-bt-text-primary py-2">
                              ${account.portfolio_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </TableCell>
                            <TableCell className="text-right py-2">
                              <span className={`font-mono text-xs font-medium ${totalPnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                                {totalPnl >= 0 ? "+" : ""}${totalPnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </TableCell>
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Trade Log */}
          {trades.length > 0 && (
            <div className="bg-bt-surface border border-bt-border-subtle" data-testid="woodrow-trades">
              <button
                onClick={() => setTradesOpen(!tradesOpen)}
                className="w-full flex items-center gap-2 px-3 py-2 border-b border-bt-border-subtle hover:bg-bt-hover transition-colors text-left"
                data-testid="toggle-woodrow-trades"
              >
                {tradesOpen ? <ChevronDown className="w-3.5 h-3.5 text-bt-text-tertiary" /> : <ChevronRight className="w-3.5 h-3.5 text-bt-text-tertiary" />}
                <span className="text-xs font-mono uppercase tracking-wider text-bt-text-secondary">Trade Log</span>
                <span className="text-xs font-mono text-bt-text-tertiary ml-auto">{trades.length}</span>
              </button>
              {tradesOpen && (
                <div className="max-h-[280px] overflow-y-auto">
                  {trades.map((t, i) => (
                    <div key={i} className="px-3 py-1.5 border-b border-bt-border-subtle last:border-0 panel-hover" data-testid={`woodrow-trade-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`px-1.5 py-0.5 text-[9px] font-mono font-bold ${
                          t.action === "BUY" ? "bg-bt-positive/15 text-bt-positive border border-bt-positive/30"
                          : t.action === "SHORT" ? "bg-purple-500/15 text-purple-400 border border-purple-500/30"
                          : t.action === "COVER" ? "bg-blue-500/15 text-blue-400 border border-blue-500/30"
                          : "bg-bt-negative/15 text-bt-negative border border-bt-negative/30"
                        }`}>{t.action}</span>
                        {t.trigger && <span className="px-1 py-0.5 text-[8px] font-mono font-bold bg-bt-negative/25 text-bt-negative border border-bt-negative/40">{t.trigger}</span>}
                        <span className="font-mono text-xs font-medium text-bt-text-primary">{t.symbol}</span>
                        <span className="font-mono text-[10px] text-bt-text-secondary">{t.shares} @ ${t.price.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {t.pnl !== undefined && (
                          <span className={`font-mono text-[10px] ${t.pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                            {t.pnl >= 0 ? "+" : ""}${t.pnl.toLocaleString()}
                          </span>
                        )}
                        <span className="font-mono text-[9px] text-bt-text-tertiary">{t.timestamp ? new Date(t.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + new Date(t.timestamp).toLocaleTimeString() : ""}</span>
                      </div>
                    </div>
                    {t.note && (
                      <p className="mt-1 text-[11px] leading-snug text-bt-text-secondary font-body" data-testid={`woodrow-trade-note-${i}`}>
                        <span className="mr-1 text-[9px] font-mono text-bt-gold">WHY</span>{t.note}
                      </p>
                    )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Performance Chart, Benchmark, Strategy */}
        <div className="space-y-4">
          {/* Performance Chart */}
          <WoodrowPerformance period={perfPeriod} onPeriodChange={setPerfPeriod} />

          {/* Benchmark Comparison */}
          <WoodrowBenchmark period={perfPeriod} />

          {/* Strategy Settings */}
          <WoodrowStrategy />

          {/* Empty state */}
          {account.total_trades === 0 && !isEvaluating && (
            <div className="text-center py-6">
              <p className="text-xs text-bt-text-tertiary font-body">Click "Run" or wait for the 15-min cycle.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
